import { CommonModule } from '@angular/common';
import { Component, ViewChild, TemplateRef, ViewContainerRef, OnDestroy, OnInit, HostBinding } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { Overlay, OverlayRef, OverlayModule } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { ElementoMenu, ELEMENTOS_MENU } from '../../constants/menu-data.constants';
import { PermisosService } from '@core/services/permisos.service';
import { PERMISOS_PLANTILLAS } from '@shared/constants/permisos';
import { ContextoService } from '@core/services/contexto.service';
import { BadgeMenuService } from '@core/services/badge-menu.service';
import { RUTAS } from '@core/constants/rutas.constants';
import { MODOS_SISTEMA } from '@core/constants/modos.constants';
import { esRolAdmin } from '@core/constants/roles.constants';
import { Subject, takeUntil } from 'rxjs';

const POPOVER_CLOSE_DELAY_MS = 100;
const POPOVER_OFFSET_X = 8;

@Component({
  selector: 'app-menu-vertical',
  imports: [ CommonModule, OverlayModule, RouterModule],
  templateUrl: 'menu-vertical.component.html',
  styleUrls: ['menu-vertical.component.scss'],
  standalone: true
})
export class MenuVerticalComponent implements OnInit, OnDestroy {
  @ViewChild('popoverTemplate', { static: true }) public popoverTemplate!: TemplateRef<unknown>;

  public estaColapsado = false;
  public elementosMenu: ElementoMenu[] = [];
  public mostrarCambiarRolEmpresa = false;
  private readonly elementosMenuOriginales: ElementoMenu[] = ELEMENTOS_MENU;
  private readonly destroy$ = new Subject<void>();

  private overlayRef: OverlayRef | null = null;
  private isHoveringPopover = false;
  private isHoveringMenuItem = false;
  private popoverCloseTimeout: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private readonly router: Router,
    private readonly overlay: Overlay,
    private readonly viewContainerRef: ViewContainerRef,
    private readonly permisosService: PermisosService,
    private readonly contextoService: ContextoService,
    private readonly badgeMenuService: BadgeMenuService
  ) {}

  @HostBinding('class.collapsed')
  public get isCollapsed(): boolean {
    return this.estaColapsado;
  }

  public ngOnInit(): void {
    this.filtrarMenuPorPermisos();
    this.evaluarMostrarCambioRolEmpresa();
    this.cargarBadges();
    this.expandirSubmenuActivo();

    this.permisosService.contextoCambiado$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.filtrarMenuPorPermisos();
        this.evaluarMostrarCambioRolEmpresa();
        this.cargarBadges();
        this.expandirSubmenuActivo();
      });
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.limpiarTimeout();
    this.cerrarPopover();
    this.resetearEstadosHover();
  }

  public alternarColapso(): void {
    this.estaColapsado = !this.estaColapsado;
    this.resetearEstadosElementosMenu();
  }

  public cambiarRolOrganizacion(): void {
    const orgRoles = this.contextoService.obtenerOrgRolesJWT() || [];
    const tieneRolAdmin = orgRoles.some(or => esRolAdmin(or.role));
    const tieneRolOperativo = orgRoles.some(or => !esRolAdmin(or.role));

    this.contextoService.limpiarContexto();

    if (tieneRolAdmin && tieneRolOperativo) {
      void this.router.navigate([RUTAS.SELECCION_MODO], {
        state: { forzarSeleccion: true }
      });
    } else {
      const modo = tieneRolAdmin ? MODOS_SISTEMA.ADMIN : MODOS_SISTEMA.OPERATIVO;
      this.contextoService.establecerModoSinContexto(modo);
      void this.router.navigate([RUTAS.PERFILES_EMPRESAS], {
        state: { modo, forzarSeleccion: true }
      });
    }
  }

  public alternarSubmenu(elemento: ElementoMenu): void {
    if (!this.estaColapsado) {
      elemento.expandido = !elemento.expandido;
    }
  }

  public alEntrarRaton(elemento: ElementoMenu, event: MouseEvent): void {
    if (!this.puedeAbrirSubmenuEnHover(elemento)) {
      return;
    }

    this.ocultarTodosLosSubmenus();
    elemento.mostrarEnHover = true;
    this.isHoveringMenuItem = true;
    this.limpiarTimeout();
    this.mostrarPopover(elemento, event);
  }

  public alSalirRaton(): void {
    if (!this.estaColapsado) {
      return;
    }

    this.isHoveringMenuItem = false;
    this.programarCierrePopover();
  }

  public onPopoverMouseEnter(): void {
    this.isHoveringPopover = true;
    this.limpiarTimeout();
  }

  public onPopoverMouseLeave(): void {
    this.isHoveringPopover = false;
    this.programarCierrePopover();
  }

  public navegarDesdePopover(ruta: string): void {
    this.limpiarTimeout();
    this.cerrarPopover();
    this.ocultarTodosLosSubmenus();
    this.resetearEstadosHover();
    this.navegarA(ruta);
  }

  public navegarA(ruta: string): void {
    if (ruta) {
      void this.router.navigate([ruta]);
    }
  }

  public esRutaActiva(ruta: string): boolean {
    return ruta === '/' ? this.router.url === '/' : this.router.url.startsWith(ruta);
  }

  public onPopoverOptionKeyDown(event: KeyboardEvent, ruta: string): void {
    if (event.key === 'Escape') {
      this.cerrarPopover();
      this.ocultarTodosLosSubmenus();
    } else if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.navegarDesdePopover(ruta);
    }
  }

  public getTooltipContent(elemento: ElementoMenu): string {
    let contenido = `${elemento.titulo}`;
    
    if (elemento.descripcion) {
      contenido += `\n${elemento.descripcion}`;
    }
    
    if (elemento.hijos && elemento.hijos.length > 0) {
      contenido += '\n\nOpciones:';
      const opcionesTexto = elemento.hijos.map(hijo => {
        const descripcionTexto = hijo.descripcion ? ' - ' + hijo.descripcion : '';
        return `• ${hijo.titulo}${descripcionTexto}`;
      }).join('\n');
      contenido += `\n${opcionesTexto}`;
    }
    
    return contenido;
  }

  public getBadgeCount(ruta: string): number {
    return this.badgeMenuService.getBadgeCount(ruta);
  }

  public debeMostrarBadge(elemento: ElementoMenu): boolean {
    return Boolean(elemento.badge) && Boolean(elemento.ruta) && this.getBadgeCount(elemento.ruta as string) > 0;
  }

  private mostrarPopover(elemento: ElementoMenu, event: MouseEvent): void {
    this.cerrarPopover();
    
    const target = event.currentTarget as HTMLElement;
    const positionStrategy = this.overlay.position()
      .flexibleConnectedTo(target)
      .withPositions([
        {
          originX: 'end',
          originY: 'center',
          overlayX: 'start',
          overlayY: 'center',
          offsetX: POPOVER_OFFSET_X
        }
      ])
      .withPush(false);

    this.overlayRef = this.overlay.create({
      positionStrategy,
      hasBackdrop: false,
      scrollStrategy: this.overlay.scrollStrategies.reposition()
    });

    const portal = new TemplatePortal(this.popoverTemplate, this.viewContainerRef, { elemento });
    this.overlayRef.attach(portal);
  }

  private cerrarPopover(): void {
    if (this.overlayRef) {
      this.overlayRef.dispose();
      this.overlayRef = null;
    }
  }

  private limpiarTimeout(): void {
    if (this.popoverCloseTimeout) {
      clearTimeout(this.popoverCloseTimeout);
      this.popoverCloseTimeout = null;
    }
  }

  private resetearEstadosHover(): void {
    this.isHoveringPopover = false;
    this.isHoveringMenuItem = false;
  }

  private ocultarTodosLosSubmenus(): void {
    for (const elemento of this.elementosMenu) {
      elemento.mostrarEnHover = false;
    }
  }

  private resetearEstadosElementosMenu(): void {
    for (const elemento of this.elementosMenu) {
      elemento.expandido = false;
      elemento.mostrarEnHover = false;
    }
  }

  private expandirSubmenuActivo(): void {
    for (const elemento of this.elementosMenu) {
      if (elemento.hijos?.some(hijo => hijo.ruta && this.esRutaActiva(hijo.ruta))) {
        elemento.expandido = true;
      }
    }
  }

  private puedeAbrirSubmenuEnHover(elemento: ElementoMenu): boolean {
    return this.estaColapsado && 
           Boolean(elemento.hijos?.length);
  }

  private programarCierrePopover(): void {
    this.popoverCloseTimeout = setTimeout(() => {
      if (!this.isHoveringMenuItem && !this.isHoveringPopover) {
        this.ocultarTodosLosSubmenus();
        this.cerrarPopover();
      }
    }, POPOVER_CLOSE_DELAY_MS);
  }

  private filtrarMenuPorPermisos(): void {
    this.elementosMenu = this.elementosMenuOriginales
      .map(elemento => this.filtrarElementoRecursivo(elemento))
      .filter((elemento): elemento is ElementoMenu => elemento !== null);
  }

  private filtrarElementoRecursivo(elemento: ElementoMenu): ElementoMenu | null {
    if (elemento.permisosAlternativos && !this.permisosService.tieneAlgunPermiso([...elemento.permisosAlternativos])) {
      return null;
    }

    if (!elemento.permisoRequerido) {
      if (elemento.hijos && elemento.hijos.length > 0) {
        const hijosFiltrados = elemento.hijos
          .map(hijo => this.filtrarElementoRecursivo(hijo))
          .filter((hijo): hijo is ElementoMenu => hijo !== null);

        if (hijosFiltrados.length === 0) {
          return null;
        }

        return { ...elemento, hijos: hijosFiltrados };
      }

      return elemento;
    }

    const tienePermiso = this.permisosService.tienePermisoSync(elemento.permisoRequerido);

    if (!tienePermiso) {
      return null;
    }

    if (elemento.hijos && elemento.hijos.length > 0) {
      const hijosFiltrados = elemento.hijos
        .map(hijo => this.filtrarElementoRecursivo(hijo))
        .filter((hijo): hijo is ElementoMenu => hijo !== null);

      return { ...elemento, hijos: hijosFiltrados };
    }

    return elemento;
  }

  private cargarBadges(): void {
    if (this.permisosService.tienePermisoSync(PERMISOS_PLANTILLAS.APROBAR_PLANTILLA)) {
      this.badgeMenuService.cargarConteoSolicitudes();
    }
  }

  private evaluarMostrarCambioRolEmpresa(): void {
    const orgRoles = this.contextoService.obtenerOrgRolesJWT() || [];
    const organizaciones = this.contextoService.obtenerOrganizacionesJWT() || [];
    
    const rolesUnicos = [...new Set(orgRoles.map(or => or.role))];
    const orgsDesdeRoles = [...new Set(orgRoles.map(or => or.orgId))];
    
    this.mostrarCambiarRolEmpresa = rolesUnicos.length > 1 || 
                                     organizaciones.length > 1 || 
                                     orgsDesdeRoles.length > 1;
  }
}
